import { type EngineeringBreak } from '../types/breaks';
import { PlatformGlyph } from './PlatformGlyph';
import {
  PLATFORM_TEXT,
  SEVERITY_BAR,
  SEVERITY_CARD_RING,
} from './break-styles';

interface BreakCardProps {
  engineeringBreak: EngineeringBreak;
  onOpen: (breakId: string) => void;
  /** Resting rotation (applied at md+), e.g. "md:-rotate-2", for the scattered collage. */
  rotationClass?: string;
}

export function BreakCard({
  engineeringBreak,
  onOpen,
  rotationClass = '',
}: BreakCardProps) {
  const isCritical = engineeringBreak.severity === 'high';
  const uniquePlatforms = Array.from(
    new Set(engineeringBreak.evidence.map((evidence) => evidence.platform))
  );

  return (
    <button
      type="button"
      onClick={() => onOpen(engineeringBreak.id)}
      className={`group relative flex w-full flex-col overflow-hidden rounded-3xl border border-zinc-100 bg-white p-6 text-start transition-all duration-500 ease-out hover:-translate-y-2 hover:!rotate-0 sm:p-7 ${
        SEVERITY_CARD_RING[engineeringBreak.severity]
      } ${rotationClass}`}
    >
      {/* Severity accent - the first thing the eye lands on */}
      <span
        className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${
          SEVERITY_BAR[engineeringBreak.severity]
        }`}
      />

      <h3 className="mb-2.5 mt-1 text-lg font-medium leading-snug text-black transition-colors group-hover:text-[#0052CC] sm:text-xl">
        {engineeringBreak.title}
      </h3>

      <p className="mb-6 line-clamp-2 text-sm leading-relaxed text-zinc-500">
        {engineeringBreak.summary}
      </p>

      <div className="mt-auto flex items-center justify-between border-t border-zinc-50 pt-5">
        <div className="flex items-center gap-3">
          <div className="flex -space-x-1.5">
            {uniquePlatforms.map((platform) => (
              <span
                key={platform}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-100 bg-white shadow-sm"
              >
                <PlatformGlyph
                  platform={platform}
                  className={`h-[18px] w-[18px] ${PLATFORM_TEXT[platform]}`}
                />
              </span>
            ))}
          </div>
          <span
            className={`text-[11px] font-medium ${
              isCritical ? 'text-rose-500' : 'text-zinc-400'
            }`}
          >
            {engineeringBreak.detectedAgo}
          </span>
        </div>
        <span className="flex items-center text-xs font-medium text-zinc-300 transition-colors group-hover:text-[#0052CC]">
          Examine
          <svg
            viewBox="0 0 24 24"
            className="ms-1 h-3.5 w-3.5 -translate-x-2 opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100 rtl:rotate-180"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </button>
  );
}
